import { Input } from "@xola/ui-kit";
import cardValidator from "card-validator/dist/index";
import { clsx } from "clsx";
import { CreditCardType } from "credit-card-type/dist/types";
import React, { ChangeEvent } from "react";
import { CreditCardIcon, CreditCardServices } from "../Common/CreditCardIcon";
import { FormatInput } from "./FormatInput";
import { formatCardNumber, getCreditCardType } from "@/helpers/rawCardHelper";

export interface RawCreditCard {
    cardNumber: string;
    cvv: string;
    expiry: string;
    zipCode: string;
    type: string;
}

const CARD_MAXLENGTH = 16;
const CARD_MAX_CVV = 3;
const Backspace = "Backspace";

interface RawCreditCardInputProps {
    card: RawCreditCard;
    onChange?: (card: RawCreditCard) => void;
}

const getMaxLength = (card: CreditCardType) => {
    let maxLength = card.lengths.at(-1);
    if (!maxLength) {
        return null;
    }

    if ("gaps" in card) {
        maxLength += card.gaps.length;
    }

    return maxLength;
};

// This component creates a credit card input component similar to what stripe has with each field having validation
// Please refer to https://www.npmjs.com/package/credit-card-type for more info
export const RawCreditCardInput = ({ card: creditCard, onChange }: RawCreditCardInputProps) => {
    const { cardNumber, cvv, expiry, zipCode } = creditCard;
    const { cardNumberError, expiryError } = validateCard(creditCard);

    const card = getCreditCardType(creditCard.cardNumber);

    const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        const card = getCreditCardType(e.target.value);
        if (card) {
            onChange?.({ ...creditCard, cardNumber: formatCardNumber(e.target.value, card), type: card.type });
        } else {
            onChange?.({ ...creditCard, cardNumber: e.target.value });
        }
    };

    const handleCardKeyDown = (e: KeyboardEvent) => {
        validateInputEvent(e);
        const isVisaCard = card?.type === CreditCardServices.VISA;
        const cardLength = isVisaCard ? CARD_MAXLENGTH : card?.lengths.at(-1) ?? CARD_MAXLENGTH;
        if (
            cardLength === cardNumber.replace(/\s/g, "").length &&
            cardNumberError.isPotentiallyValid &&
            !isBackspacePressed(e)
        ) {
            goToNextOrPreviousInput("#authNetExpiry", expiry.length + 1);
        }
    };

    const handleExpiryChange = (expiry: string) => {
        const expiryMonth = expiry.length === 1 && Number.parseInt(expiry, 10) >= 2 ? `0${expiry}` : expiry.slice(0, 2);
        const expiryYear = expiry.slice(2, 4);
        onChange?.({ ...creditCard, expiry: `${expiryMonth}${expiryYear}` });
    };

    const handleExpiryKeyDown = (e: any) => {
        validateInputEvent(e);
        if (expiry.length === 4 && expiryError.isPotentiallyValid && !isBackspacePressed(e)) {
            goToNextOrPreviousInput("#authNetCVV", cvv.length);
        }

        if (isBackspacePressed(e) && expiry.length === 0) {
            goToNextOrPreviousInput("#authNetCardInput", cardNumber.length);
        }
    };

    const handleCVVChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.({ ...creditCard, cvv: e.target.value });
    };

    const handleCVVKeyDown = (e: KeyboardEvent) => {
        validateInputEvent(e);
        if (cvv.length === (card?.code.size ?? CARD_MAX_CVV)) {
            goToNextOrPreviousInput("#authNetZipCode", zipCode.length);
        }

        if (isBackspacePressed(e) && cvv.length === 0) {
            goToNextOrPreviousInput("#authNetExpiry", expiry.length + 1);
        }
    };

    const handleZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.({ ...creditCard, zipCode: e.target.value });
    };

    const handleZipCodeKeyDown = (e: KeyboardEvent) => {
        validateZipCode(e);
        if (isBackspacePressed(e) && zipCode.length === 0) {
            goToNextOrPreviousInput("#authNetCVV", cvv.length);
        }
    };

    return (
        <div className="fs-exclude flex">
            <div className="flex grow-0 items-center pl-2">
                <CreditCardIcon
                    name={card?.type ?? ""}
                    className={clsx("border-gray-lighter", { "text-danger": !cardNumberError.isPotentiallyValid })}
                />
            </div>
            <Input
                id="authNetCardInput"
                name="cardNumber"
                placeholder="Card Number"
                className={clsx("grow basis-3/12 border-none bg-transparent", {
                    "!text-danger": !cardNumberError.isPotentiallyValid,
                })}
                maxLength={card ? getMaxLength(card) ?? 16 : 16} // different cards can have different lengths, like visa has 13, 16, 19, we are using 16 as the maxlength for visa otherwise it will be 19
                value={cardNumber}
                onKeyDown={handleCardKeyDown}
                onChange={handleCardNumberChange}
            />
            <div className="ml-auto flex">
                <FormatInput
                    id="authNetExpiry"
                    format={format}
                    placeholder="MM/YY"
                    className={clsx("!w-15 border-none bg-transparent !px-0", {
                        "!text-danger": !expiryError.isPotentiallyValid,
                    })}
                    value={expiry}
                    onValueChange={({ value }) => handleExpiryChange(value)}
                    onKeyDown={handleExpiryKeyDown}
                />

                <CVVInput
                    value={cvv}
                    maxlength={card?.code.size ?? CARD_MAX_CVV}
                    onKeyDown={handleCVVKeyDown}
                    onChange={handleCVVChange}
                />

                <Input
                    id="authNetZipCode"
                    className="!mr-2 !w-15 border-none bg-transparent !px-0"
                    placeholder="ZIP"
                    value={zipCode}
                    onKeyDown={handleZipCodeKeyDown}
                    onChange={handleZipCodeChange}
                />
            </div>
        </div>
    );
};

interface CvvInputProps {
    value: string;
    maxlength: number;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: KeyboardEvent) => void;
}

const CVVInput = ({ value, maxlength, onChange, onKeyDown }: CvvInputProps) => {
    return (
        <Input
            id="authNetCVV"
            placeholder="CVV"
            className="!w-10 border-none bg-transparent !px-0"
            maxLength={maxlength}
            value={value}
            onKeyDown={onKeyDown}
            onChange={onChange}
        />
    );
};

export const getInitialCardValues = () => {
    return {
        cardNumber: "",
        cvv: "",
        expiry: "",
        zipCode: "",
        type: "",
    };
};

export const validateCard = (card: RawCreditCard) => {
    const { cardNumber, expiry } = card;

    const cardNumberError = cardValidator.number(cardNumber);
    const expiryError = cardValidator.expirationDate(expiry);

    return { cardNumberError, expiryError };
};

const limit = (value: string, max: string) => {
    if (value.length === 1 && value[0] > max[0]) {
        value = "0" + value;
    }

    if (value.length === 2) {
        if (Number(value) === 0) {
            value = "01";
        } else if (value > max) {
            value = max;
        }
    }

    return value;
};

const format = (value: string) => {
    const month = limit(value.slice(0, 2), "12");
    const year = value.slice(2, 4);

    return month + (year.length > 0 ? "/" + year : "");
};

const validateInputEvent = (e: any) => {
    if (Number.isNaN(Number(e.key)) && !isBackspacePressed(e)) {
        e.preventDefault();
    }
};

const validateZipCode = (e: any) => {
    const alphaNumeric = /^[\da-zA-Z]+$/;
    if (!isBackspacePressed(e) && !alphaNumeric.test(e.key)) {
        e.preventDefault();
    }
};

const goToNextOrPreviousInput = (nextFieldId: string, offset: number) => {
    const input = document.querySelector<HTMLInputElement>(nextFieldId);
    input?.setSelectionRange(offset, offset);
    input?.focus();
};

const isBackspacePressed = (e: any) => {
    return e.key === Backspace;
};
